import { css, cx } from '@repo/pandacss/styled-system/css';
import { debounce } from 'lodash';
import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { Checkbox } from './Checkbox';

type Props = {
  option: { label: string; count: number };
  isSelected: boolean;
  onSelect: () => void;
  tabIndex: number;
};

const itemClass = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: 'full',
  paddingInlineStart: '4',
  paddingInlineEnd: '4',
  paddingBlockStart: '2',
  paddingBlockEnd: '2',
  cursor: 'pointer',
  backgroundColor: 'neutral.white',
  backgroundImage: '[none]',
  borderTopStyle: 'none',
  borderRightStyle: 'none',
  borderBottomStyle: 'none',
  borderLeftStyle: 'none',
  textAlign: 'left',
  fontFamily: '[inherit]',
  _hover: { backgroundColor: 'neutral.lightest' },
  _focusVisible: {
    outlineWidth: 'medium',
    outlineStyle: 'solid',
    outlineColor: 'blue.light',
    outlineOffset: '[-2px]',
  },
});

const itemDisabledClass = css({
  cursor: 'default',
  _hover: { backgroundColor: 'transparent' },
});

const labelClass = css({ fontSize: 'sm', color: 'neutral.dark' });
const rightClass = css({ display: 'flex', alignItems: 'center', columnGap: '2', rowGap: '2' });
const countClass = css({ textStyle: 'footnote', color: 'neutral.dark' });
const countDisabledClass = css({ color: 'neutral.lighter' });

export const OptionItem = forwardRef<HTMLButtonElement, Props>(
  ({ option, isSelected, onSelect, tabIndex }, ref) => {
    const stableOnSelectRef = useRef(onSelect);
    useEffect(() => {
      stableOnSelectRef.current = onSelect;
    }, [onSelect]);

    const debouncedOnSelect = useCallback(
      debounce(() => stableOnSelectRef.current(), 100, { leading: true, trailing: false }),
      [],
    );
    useEffect(() => () => debouncedOnSelect.cancel(), [debouncedOnSelect]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          debouncedOnSelect();
        }
      },
      [debouncedOnSelect],
    );

    const isDisabled = option.count === 0 && !isSelected;

    return (
      <button
        ref={ref}
        type="button"
        role="option"
        aria-selected={isSelected}
        tabIndex={tabIndex}
        onKeyDown={isDisabled ? undefined : handleKeyDown}
        onClick={isDisabled ? undefined : debouncedOnSelect}
        className={cx(itemClass, isDisabled && itemDisabledClass)}
      >
        <span className={labelClass}>{option.label}</span>
        <span className={rightClass}>
          <span className={`${countClass} ${isDisabled ? countDisabledClass : ''}`}>
            {option.count}
          </span>
          <Checkbox checked={isSelected} onChange={debouncedOnSelect} disabled={isDisabled} />
        </span>
      </button>
    );
  },
);
